import {useCallback, useEffect, useRef, useState} from 'react';
import {useApolloClient, useMutation} from '@apollo/client';
import {useTranslation} from 'react-i18next';
import _ from 'lodash';
import {CreateInstalledBaseNode} from '../../graphql/mutations/createInstalledBaseNode';
import {resolveErrorMessage} from '../../utils/resolveErrorMessage';
import {DuplicateItem} from './types';

interface Args {
    buildVariables: (title: string) => Record<string, any>;
    refetchFunction?: () => void;
}

/**
 * Creates installed base nodes strictly one after another and tracks per-item
 * progress. Cancelling (or unmounting) stops the queue after the in-flight
 * mutation; already created nodes remain.
 */
export const useSequentialNodeCreation = ({buildVariables, refetchFunction}: Args) => {
    const client = useApolloClient();
    const {t} = useTranslation();
    const [createNode] = useMutation(CreateInstalledBaseNode);

    const [items, setItems] = useState<DuplicateItem[]>([]);
    const [started, setStarted] = useState(false);
    const [running, setRunning] = useState(false);
    const [cancelled, setCancelled] = useState(false);
    const cancelRef = useRef(false);

    // Stop the queue when the wizard unmounts (e.g. modal closed via backdrop).
    useEffect(() => () => {
        cancelRef.current = true;
    }, []);

    const updateItem = (id: number, patch: Partial<DuplicateItem>) => {
        setItems((prev) => prev.map((item) => (item.id === id ? {...item, ...patch} : item)));
    };

    const refetchLists = useCallback(() => {
        client.refetchQueries({include: ['InstalledBaseNodeList']});
        if (refetchFunction) {
            refetchFunction();
        }
    }, [client, refetchFunction]);

    const createOne = useCallback(async (id: number, title: string): Promise<boolean> => {
        updateItem(id, {status: 'inProgress', errorMessage: undefined});
        const startedAt = performance.now();
        try {
            const res = await createNode({variables: buildVariables(title)});
            updateItem(id, {
                status: 'done',
                durationMs: performance.now() - startedAt,
                createdNodeId: _.get(res, 'data.createInstalledBaseNode.id'),
            });
            return true;
        } catch (err) {
            updateItem(id, {status: 'error', errorMessage: resolveErrorMessage(err, t)});
            return false;
        }
    }, [createNode, buildVariables, t]);

    const start = useCallback(async (titles: string[]) => {
        const newItems: DuplicateItem[] = titles.map((title, i) => ({id: i, title, status: 'pending'}));
        setItems(newItems);
        setStarted(true);
        setCancelled(false);
        cancelRef.current = false;
        setRunning(true);

        let anySuccess = false;
        for (const item of newItems) {
            if (cancelRef.current) break;
            const ok = await createOne(item.id, item.title);
            anySuccess = anySuccess || ok;
        }

        setRunning(false);
        if (anySuccess) {
            refetchLists();
        }
    }, [createOne, refetchLists]);

    const retry = useCallback(async (item: DuplicateItem) => {
        const ok = await createOne(item.id, item.title);
        if (ok) {
            refetchLists();
        }
    }, [createOne, refetchLists]);

    const cancel = useCallback(() => {
        cancelRef.current = true;
        setCancelled(true);
    }, []);

    const successCount = items.filter((item) => item.status === 'done').length;
    const errorCount = items.filter((item) => item.status === 'error').length;
    const completedCount = successCount + errorCount;
    const percent = items.length > 0 ? (completedCount / items.length) * 100 : 0;

    const doneDurations = items
        .map((item) => item.durationMs)
        .filter((d): d is number => typeof d === 'number');
    const remainingCount = items.filter(
        (item) => item.status === 'pending' || item.status === 'inProgress'
    ).length;
    const etaMs = running && doneDurations.length > 0
        ? (doneDurations.reduce((sum, d) => sum + d, 0) / doneDurations.length) * remainingCount
        : null;

    return {
        items,
        start,
        retry,
        cancel,
        started,
        running,
        cancelled,
        successCount,
        errorCount,
        percent,
        etaMs,
    };
};
